import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Models from 'Models/Entities';

export interface IParkingCapacityRestrictionEntityAttributes extends IModelAttributes {
	name: string;
	configuration: string;
	parkingLocationId: string;
	parkingLocation: Models.ParkingLocationEntity | Models.IParkingLocationEntityAttributes;
}

@entity('ParkingCapacityRestrictionEntity', 'Parking Capacity Restriction')
export default class ParkingCapacityRestrictionEntity
	extends Model
	implements IParkingCapacityRestrictionEntityAttributes
{
	private static configVirtualFields = ['configMax', 'configOverbookingDisabled', 'configShowCapacity'];

	/**
	 *
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [...ParkingCapacityRestrictionEntity.configVirtualFields];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [...ParkingCapacityRestrictionEntity.configVirtualFields];

	@observable
	@attribute<ParkingCapacityRestrictionEntity, string>()
	public name: string;

	@observable
	@attribute<ParkingCapacityRestrictionEntity, string>()
	public configuration: string;

	// --- Virtual fields for structured configuration editing ---

	@observable
	public configMax: number = 0;

	@observable
	public configOverbookingDisabled: boolean = false;

	@observable
	public configShowCapacity: boolean = false;

	// --- End virtual fields ---

	@observable
	@attribute()
	public parkingLocationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingLocation: Models.ParkingLocationEntity;

	constructor(attributes?: Partial<IParkingCapacityRestrictionEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IParkingCapacityRestrictionEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.name)) {
				this.name = attributes.name;
			}
			if (isNotNullOrUndefined(attributes.configuration)) {
				this.configuration = attributes.configuration;
				this.parseConfigurationToFields();
			}
			if (isNotNullOrUndefined(attributes.parkingLocationId)) {
				this.parkingLocationId = attributes.parkingLocationId;
			}
			if (isNotNullOrUndefined(attributes.parkingLocation)) {
				if (attributes.parkingLocation === null) {
					this.parkingLocation = attributes.parkingLocation;
				} else if (attributes.parkingLocation instanceof Models.ParkingLocationEntity) {
					this.parkingLocation = attributes.parkingLocation;
					this.parkingLocationId = attributes.parkingLocation.id;
				} else {
					this.parkingLocation = new Models.ParkingLocationEntity(attributes.parkingLocation);
					this.parkingLocationId = this.parkingLocation.id;
				}
			}
		}
	}

	/**
	 * Parses the configuration JSON string into individual observable fields.
	 */
	private parseConfigurationToFields() {
		if (!this.configuration) return;
		try {
			const config = JSON.parse(this.configuration);
			this.configMax = config.Max ?? 0;
			this.configOverbookingDisabled = config.OverbookingDisabled ?? false;
			this.configShowCapacity = config.ShowCapacity ?? false;
		} catch {
			// If parsing fails, leave defaults
		}
	}
}
